import { collection } from '@/infra/collection'
import { Model, type ModelProps } from '@/infra/model'
import { observable, action } from 'mobx'
import { Id } from '@/infra/id'
import { Message } from './Message'
import { unixEpoch } from '@/utils/date-util'
import { Participant, type ParticipantProps } from './Participant'
import { filter, takeUntil, share, find, map } from 'rxjs/operators'
import { type ConversationMeta } from './conversation-meta'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { messageStatusDeltaStream } from './message-status-delta-stream'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil } from '@/utils/rx-util'
import { max } from 'date-fns'
import { once } from 'lodash-es'

export interface ConversationProps extends Omit<
  ModelProps<Conversation>,
  'participants'
> {
  participants?: Partial<ParticipantProps>[]
}

/**
 * A conversation between the participants of a job.
 */
export class Conversation extends Model<Conversation> {
  static get none() {
    return lazyNone()
  }

  id = Id.none

  @observable accessor active = false
  @observable accessor name = 'Conversation'
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor unreadCount = 0
  @observable accessor humanMessagesCount = 0
  @observable accessor workspace = Workspace.none
  @observable accessor meta: ConversationMeta = {
    type: 'Unknown',
  }

  readonly participants = collection({
    getDataId: (d) => d.userPublicId,
    getModelId: (m) => m.userPublicId,
    create: () => new Participant(),
  })

  readonly messages = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => this._createMessage(),
  })

  @action.bound
  override set({ participants, ...props }: Partial<ConversationProps>): this {
    this.participants.reset(participants)
    return super.set(props)
  }

  /**
   * Resets the unread count to the specified value.
   *
   * @param count
   */
  @action.bound
  resetUnreadCount(count: number) {
    this.unreadCount = count
  }

  /**
   * Increases or decreases the unread count by the specified amount.
   *
   * @param delta
   */
  @action.bound
  varyUnreadCount(delta: number) {
    this.unreadCount = Math.max(this.unreadCount + delta, 0)
  }

  /**
   * Sets the human messages count.
   *
   * @param count
   */
  @action.bound
  setHumanMessagesCount(count: number) {
    this.humanMessagesCount = count
  }

  private _createMessage() {
    const message = new Message(this)

    // An observable that emits a value and completes when the
    // message is removed from the collection.
    const messageRemoved$ = this.messages.removals$.pipe(
      find((m) => m === message),
      share(),
    )

    // Whenever we see a timestamp on the message that is newer than that of the
    // conversation, update the one on the conversation.
    toStream(() => max([message.dateCreated, message.dateModified]), true)
      .pipe(
        map((date) => max([date, this.dateModified])),
        takeUntil(messageRemoved$),
      )
      .subscribe(
        action((date: Date) => {
          this.dateModified = date
        }),
      )

    // Status changes pertaining to the current user.
    const myStatusChanges$ = message.statusChanges$.pipe(
      filter((status) => status.member.user.isCurrentUser),
    )

    // Once we have the CURRENT message delivery status for the current user,
    // if it's set to Read, then there's nothing left to track for that message,
    // so we need to stop the delta flow. Otherwise, a MessageDeleted event would wrongfully
    // decrement the unread count even if it was already read.
    const stop$ = toStream(
      () =>
        message.recipientStatuses.find((r) => r.member.user.isCurrentUser)
          ?.deliveryStatus,
      true,
    ).pipe(
      filterNonNil(),
      find((s) => s === 'Read'),
    )

    // Stream of deltas used to vary the unread count.
    messageStatusDeltaStream(myStatusChanges$, messageRemoved$)
      .pipe(takeUntil(stop$))
      .subscribe(this.varyUnreadCount)

    return message
  }
}

const lazyNone = once(() => new Conversation())
